/* eslint-disable @typescript-eslint/no-namespace */
import { ServiceDefinition, ServiceProvider } from "../configuration";
import { type DocumentTemplate } from "./DocumentTemplate";
import { type EmailTemplate } from "./EmailTemplate";

export interface Template {
    /**
     * The service ID of the template.
     */
    serviceId: ServiceDefinition.Id;
    /**
     * The provider ID of the template.
     */
    serviceProviderId: ServiceProvider.Id;
    /**
     * The short ID component of the custom service definition ID.
     */
    shortId: string;
}

export namespace Template {
    /**
     * Creates a new template ID.
     *
     * @param templateName The template name.
     * @param providerId Optional provider ID for provider-specific templates.
     * @param serviceId Optional service ID for service-specific templates. Can be a CustomServiceDefinition.Id (providerId:serviceId).
     * @returns The new template ID.
     * @throws An error if the template name is invalid or parameters are inconsistent.
     */
    export const createId = <R extends EmailTemplate.Id | DocumentTemplate.Id>(
        templateName: EmailTemplate.Name | DocumentTemplate.Name,
        validTemplateNames: readonly unknown[],
        providerId?: ServiceProvider.Id,
        serviceId?: ServiceDefinition.Id,
        shortId?: string
    ) => {
        if (validTemplateNames?.length && !validTemplateNames.includes(templateName)) {
            throw new Error(
                `Template: Invalid template name '${templateName}'. ` +
                    `Must be one of: ${validTemplateNames.join(", ")}`
            );
        }

        // Build the key based on provided parameters
        let key: string;
        if (serviceId) {
            // Check if serviceId is a CustomServiceDefinition.Id (contains colon)
            if ((serviceId as string).includes(":")) {
                // serviceId already contains providerId:serviceId, just append templateName
                key = `${serviceId}:${templateName}`;
            } else if (providerId) {
                if (!shortId) {
                    throw new Error(
                        `Template: Short ID is required for provider "${providerId}" and service "${serviceId}".`
                    );
                }
                // Standard serviceId with providerId
                key = `${providerId}:${serviceId}:${shortId}:${templateName}`;
            } else {
                // Only serviceId provided (shouldn't happen in practice)
                key = `${serviceId}:${templateName}`;
            }
        } else if (providerId) {
            key = `${providerId}:${templateName}`;
        } else {
            key = templateName;
        }

        return key as R;
    };

    /**
     * Creates a new template name.
     *
     * @param value The value to create the name from.
     * @returns The new template name.
     * @throws An error if the name is invalid.
     */
    export const Name = <R extends EmailTemplate.Name | DocumentTemplate.Name>(
        value: string,
        templateNames: readonly unknown[]
    ) => {
        if (!templateNames.includes(value as R)) {
            throw new Error(
                `Template: Invalid template name '${value}'. ` + `Must be one of: ${templateNames.join(", ")}`
            );
        }
        return value as R;
    };

    /**
     * Parses a template ID to extract its components.
     *
     * @param id The template ID.
     * @returns An object with templateName, providerId, and serviceId.
     * @throws An error if the ID is empty.
     *
     * Template ID formats:
     * - templateName (1 part)
     * - providerId:templateName (2 parts)
     * - providerId:serviceId:templateName (3 parts) - where serviceId can be a CustomServiceDefinition.Id (providerId:serviceId)
     */
    export const parse = <R extends EmailTemplate.Name | DocumentTemplate.Name>(
        id: EmailTemplate.Id | DocumentTemplate.Id
    ): {
        templateName: R;
        providerId?: ServiceProvider.Id;
        serviceId?: ServiceDefinition.Id;
        shortId?: string;
    } => {
        const parts = (id as string).split(":");

        if (parts.length === 4 && parts[0] && parts[1] && parts[2] && parts[3]) {
            // Format: providerId:serviceId:shortId:templateName (CustomServiceDefinition with shortId)
            return {
                providerId: ServiceProvider.Id(parts[0]),
                serviceId: ServiceDefinition.Id(parts[1]), // Full CustomServiceDefinition.Id with shortId
                shortId: parts[2],
                templateName: parts[3] as R,
            };
        } else if (parts.length === 3 && parts[0] && parts[1]) {
            return {
                providerId: ServiceProvider.Id(parts[0]),
                serviceId: ServiceDefinition.Id(parts[1]), // Full CustomServiceDefinition.Id
                templateName: parts[2] as R,
            };
        } else if (parts.length === 2 && parts[0]) {
            return {
                providerId: ServiceProvider.Id(parts[0]),
                templateName: parts[1] as R,
            };
        } else {
            return {
                templateName: parts[0] as R,
            };
        }
    };
}
